import { Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";

/**
 * Reusable confirm dialog (replaces native window.confirm).
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   <ConfirmDialog
 *     open={open}
 *     onOpenChange={setOpen}
 *     title="..."
 *     description="..."
 *     confirmLabel="..."
 *     onConfirm={async () => { ... }}
 *     tone="danger" | "primary" | "neutral"
 *   />
 */
export default function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirmer",
  cancelLabel = "Annuler",
  onConfirm,
  loading = false,
  tone = "primary",
  testId,
}) {
  const tones = {
    primary: "#2563EB",
    danger: "#EF4444",
    neutral: "#0F172A",
  };
  const color = tones[tone] || tones.primary;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        data-testid={testId}
        className="max-w-[420px] rounded-[20px]"
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[18px] font-bold text-slate-900">
            {title}
          </AlertDialogTitle>
          {description ? (
            <AlertDialogDescription className="text-[13.5px] text-slate-600 leading-snug whitespace-pre-line">
              {description}
            </AlertDialogDescription>
          ) : null}
        </AlertDialogHeader>
        <AlertDialogFooter className="mt-1 flex gap-2 sm:gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            data-testid={testId ? `${testId}-cancel` : undefined}
            className="flex-1 rounded-[14px] border border-slate-200 bg-white px-5 py-3 text-[14px] font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            data-testid={testId ? `${testId}-confirm` : undefined}
            className="flex-1 rounded-[14px] px-5 py-3 text-[14px] font-bold text-white transition disabled:opacity-70"
            style={{
              background: color,
              boxShadow: `0 4px 18px -2px ${color}88`,
            }}
          >
            {loading ? (
              <span className="inline-flex items-center justify-center gap-2">
                <Loader2 size={16} className="animate-spin" />
                Patientez…
              </span>
            ) : (
              confirmLabel
            )}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
